import { useState } from "react";
import AdminLayout from "@/components/layout-admin";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Copy, UserPlus, DollarSign, Users, Link as LinkIcon } from "lucide-react";

type AffiliateProfile = {
  id: number;
  userId: number;
  referralCode: string;
  commissionPct: number;
  status: "active" | "inactive";
  totalEarnings: number;
  totalReferrals: number;
  payoutMethod: string | null;
  payoutDetails: string | null;
  username: string;
  fullName: string;
  email: string;
};

const emptyCreateForm = {
  username: "",
  password: "",
  fullName: "",
  email: "",
  phone: "",
  commissionPct: "10",
};

const emptyEditForm = {
  id: 0,
  commissionPct: "10",
  status: "active" as string,
  payoutMethod: "",
  payoutDetails: "",
};

export default function AdminAffiliates() {
  const { toast } = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [editForm, setEditForm] = useState(emptyEditForm);

  const { data: affiliates = [], isLoading } = useQuery<AffiliateProfile[]>({
    queryKey: ["/api/admin/affiliates"],
  });

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      await apiRequest("POST", "/api/admin/affiliates", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/affiliates"] });
      setCreateOpen(false);
      setCreateForm(emptyCreateForm);
      toast({ title: "Affiliate created" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Record<string, unknown> }) => {
      await apiRequest("PUT", `/api/admin/affiliates/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/affiliates"] });
      setEditOpen(false);
      toast({ title: "Affiliate updated" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/admin/affiliates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/affiliates"] });
      toast({ title: "Affiliate deleted" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const handleCreate = () => {
    if (!createForm.username || !createForm.password) {
      toast({ title: "Error", description: "Username and password are required", variant: "destructive" });
      return;
    }
    createMutation.mutate({
      username: createForm.username,
      password: createForm.password,
      fullName: createForm.fullName || undefined,
      email: createForm.email || undefined,
      phone: createForm.phone || undefined,
      commissionPct: parseFloat(createForm.commissionPct) || 10,
    });
  };

  const handleEdit = (a: AffiliateProfile) => {
    setEditForm({
      id: a.id,
      commissionPct: String(a.commissionPct ?? 10),
      status: a.status || "active",
      payoutMethod: a.payoutMethod || "",
      payoutDetails: a.payoutDetails || "",
    });
    setEditOpen(true);
  };

  const handleUpdate = () => {
    updateMutation.mutate({
      id: editForm.id,
      data: {
        commissionPct: parseFloat(editForm.commissionPct) || 10,
        status: editForm.status,
        payoutMethod: editForm.payoutMethod || null,
        payoutDetails: editForm.payoutDetails || null,
      },
    });
  };

  const copyReferralLink = (code: string) => {
    const link = `${window.location.origin}/marketplace?ref=${code}`;
    navigator.clipboard.writeText(link).then(() => {
      toast({ title: "Copied", description: "Referral link copied to clipboard" });
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-2xl font-bold" data-testid="text-affiliates-title">
              Affiliate Management
            </h1>
            <p className="text-muted-foreground">
              Manage affiliate partners, referral codes, and commission rates
            </p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="button-add-affiliate">
                <Plus className="w-4 h-4 mr-2" /> Add Affiliate
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Affiliate</DialogTitle>
                <DialogDescription>Create a new affiliate partner account with a unique referral code.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Full Name</Label>
                  <Input
                    value={createForm.fullName}
                    onChange={(e) => setCreateForm((p) => ({ ...p, fullName: e.target.value }))}
                    placeholder="John Doe"
                    data-testid="input-affiliate-fullname"
                  />
                </div>
                <div>
                  <Label>Username</Label>
                  <Input
                    value={createForm.username}
                    onChange={(e) => setCreateForm((p) => ({ ...p, username: e.target.value }))}
                    placeholder="johndoe"
                    data-testid="input-affiliate-username"
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={createForm.password}
                    onChange={(e) => setCreateForm((p) => ({ ...p, password: e.target.value }))}
                    placeholder="Enter password"
                    data-testid="input-affiliate-password"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={createForm.email}
                    onChange={(e) => setCreateForm((p) => ({ ...p, email: e.target.value }))}
                    placeholder="john@example.com"
                    data-testid="input-affiliate-email"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={createForm.phone}
                    onChange={(e) => setCreateForm((p) => ({ ...p, phone: e.target.value }))}
                    placeholder="+1 555-0100"
                    data-testid="input-affiliate-phone"
                  />
                </div>
                <div>
                  <Label>Commission %</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={createForm.commissionPct}
                    onChange={(e) => setCreateForm((p) => ({ ...p, commissionPct: e.target.value }))}
                    placeholder="10"
                    data-testid="input-affiliate-commission"
                  />
                </div>
                <Button
                  className="w-full"
                  onClick={handleCreate}
                  disabled={createMutation.isPending}
                  data-testid="button-create-affiliate"
                >
                  {createMutation.isPending ? "Creating..." : "Create Affiliate"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground" data-testid="text-loading">Loading affiliates...</div>
        ) : affiliates.length === 0 ? (
          <div className="text-center py-12" data-testid="text-empty">
            <UserPlus className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No affiliates yet. Add your first affiliate partner.</p>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {affiliates.map((a) => (
              <Card key={a.id} data-testid={`card-affiliate-${a.id}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium truncate" data-testid={`text-affiliate-name-${a.id}`}>
                        {a.fullName || a.username}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">@{a.username}</p>
                      {a.email && (
                        <p className="text-xs text-muted-foreground truncate">{a.email}</p>
                      )}
                    </div>
                    <Badge
                      variant={a.status === "active" ? "default" : "secondary"}
                      data-testid={`badge-status-${a.id}`}
                    >
                      {a.status}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-xs text-muted-foreground">Commission</p>
                      <p className="font-medium text-sm" data-testid={`text-commission-${a.id}`}>
                        {a.commissionPct}%
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Earnings</p>
                      <p className="font-medium text-sm" data-testid={`text-earnings-${a.id}`}>
                        ${Number(a.totalEarnings || 0).toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Referrals</p>
                      <p className="font-medium text-sm" data-testid={`text-referrals-${a.id}`}>
                        {a.totalReferrals || 0}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0 bg-muted rounded-md px-2 py-1">
                      <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                        <LinkIcon className="w-3 h-3 shrink-0" />
                        <span data-testid={`text-referral-code-${a.id}`}>ref={a.referralCode}</span>
                      </p>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => copyReferralLink(a.referralCode)}
                      data-testid={`button-copy-link-${a.id}`}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(a)}
                      data-testid={`button-edit-affiliate-${a.id}`}
                    >
                      <Pencil className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          data-testid={`button-delete-affiliate-${a.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" /> Remove
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Affiliate</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{a.fullName || a.username}"? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteMutation.mutate(a.id)}
                            data-testid="button-confirm-delete"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Affiliate</DialogTitle>
              <DialogDescription>Update affiliate commission rate and payout details.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Commission %</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={editForm.commissionPct}
                  onChange={(e) => setEditForm((p) => ({ ...p, commissionPct: e.target.value }))}
                  data-testid="edit-input-commission"
                />
              </div>
              <div>
                <Label>Status</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Button
                    variant={editForm.status === "active" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setEditForm((p) => ({ ...p, status: "active" }))}
                    data-testid="edit-button-status-active"
                  >
                    Active
                  </Button>
                  <Button
                    variant={editForm.status === "inactive" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setEditForm((p) => ({ ...p, status: "inactive" }))}
                    data-testid="edit-button-status-inactive"
                  >
                    Inactive
                  </Button>
                </div>
              </div>
              <div>
                <Label>Payout Method</Label>
                <Input
                  value={editForm.payoutMethod}
                  onChange={(e) => setEditForm((p) => ({ ...p, payoutMethod: e.target.value }))}
                  placeholder="e.g. bank_transfer, paypal"
                  data-testid="edit-input-payout-method"
                />
              </div>
              <div>
                <Label>Payout Details</Label>
                <Input
                  value={editForm.payoutDetails}
                  onChange={(e) => setEditForm((p) => ({ ...p, payoutDetails: e.target.value }))}
                  placeholder="e.g. account number or email"
                  data-testid="edit-input-payout-details"
                />
              </div>
              <Button
                className="w-full"
                onClick={handleUpdate}
                disabled={updateMutation.isPending}
                data-testid="button-update-affiliate"
              >
                {updateMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
